/**
 * Acesso dos usuários de uma empresa: grupos com permissão por área (ver ou alterar). Vale no servidor (toda rota
 * de /api/v1 passa por permOfApi em auth.ts) e no painel (menu e telas, por permOfPath e can()).
 *
 *   Dono (memberships.role = owner): tudo, e só ele mexe em usuários, grupos e permissões.
 *   Grupo Super admin: tudo, menos usuários e grupos.
 *   Demais grupos (Admin, Operador, SAC, Farmacêutico, Logística, Financeiro e os criados pelo dono): a permissão de
 *   cada área; quem está em vários grupos soma as permissões (vale a maior).
 *   Aprovar ou recusar receita: só quem está marcado com CRF (farmacêutico), seja qual for o grupo; o CRF também dá
 *   acesso às receitas.
 *
 * Os grupos prontos vêm com DEFAULT_PERMS; o dono pode mudar (fica gravado em access_groups) e criar outros.
 * memberships.role antigo (admin, sac, pharmacist, super_admin) conta como estar no grupo de mesmo nome (compatível
 * com o painel master e com contas antigas); a migração 0070 passou todos para grupos.
 */
export type TenantRole = 'owner' | 'super_admin' | 'admin' | 'sac' | 'pharmacist' | 'member';
export const TENANT_ROLES: TenantRole[] = ['owner', 'super_admin', 'admin', 'sac', 'pharmacist', 'member'];
export const ROLE_LABEL: Record<TenantRole, string> = {
  owner: 'Dono', super_admin: 'Super admin', admin: 'Admin', sac: 'SAC', pharmacist: 'Farmacêutico', member: 'Membro',
};

export type Level = 'read' | 'write';

export const PERMISSIONS = [
  { key: 'overview', section: 'Geral', label: 'Visão geral', write: null },
  { key: 'catalog', section: 'Catálogo', label: 'Catálogo, produtos e kits', write: 'Criar e alterar produtos e kits, importar' },
  { key: 'sales', section: 'Vendas', label: 'Vendas e resultados', write: null },
  { key: 'orders', section: 'Operação', label: 'Pedidos', write: 'Cancelar, nota fiscal, etiquetas e alertas' },
  { key: 'deliveries', section: 'Operação', label: 'Entregas', write: 'Agir nas entregas' },
  { key: 'queue', section: 'Operação', label: 'Fila de envios', write: 'Reprocessar envios' },
  { key: 'questions', section: 'Operação', label: 'Perguntas', write: 'Responder e apagar perguntas' },
  { key: 'messages', section: 'Operação', label: 'Mensagens pós-venda', write: 'Enviar mensagens' },
  { key: 'claims', section: 'Operação', label: 'Reclamações e devoluções', write: 'Responder, aceitar devolução, mediação, provas' },
  { key: 'claims_refund', section: 'Operação', label: 'Devolver dinheiro em reclamação', write: 'Reembolso total ou parcial' },
  { key: 'prescriptions', section: 'Operação', label: 'Receitas', write: 'Cancelar o pedido da receita (aprovar exige CRF)' },
  { key: 'listings', section: 'Anúncios', label: 'Anúncios', write: 'Anunciar, editar, pausar e apagar' },
  { key: 'products_listings', section: 'Anúncios', label: 'Produtos × anúncios', write: 'Agir na lista' },
  { key: 'prices', section: 'Anúncios', label: 'Preço e estoque', write: 'Mudar preço, regras e bloqueios' },
  { key: 'shipping', section: 'Anúncios', label: 'Envio dos anúncios', write: 'Mudar o envio' },
  { key: 'catalog_optin', section: 'Anúncios', label: 'Entrar no catálogo', write: 'Entrar no catálogo' },
  { key: 'no_catalog', section: 'Anúncios', label: 'Sem catálogo', write: 'Preparar e publicar' },
  { key: 'publications', section: 'Anúncios', label: 'Publicações', write: 'Publicar e mudar categorias' },
  { key: 'reconcile', section: 'Anúncios', label: 'Conciliar vínculos', write: 'Vincular' },
  { key: 'opportunities', section: 'Anúncios', label: 'Oportunidades', write: null },
  { key: 'buybox', section: 'Anúncios', label: 'Buy Box e concorrentes', write: 'Aplicar preço e seguir concorrentes' },
  { key: 'finance', section: 'Financeiro', label: 'Financeiro: conciliação, repasses e despesas', write: 'Lançar despesas e reprocessar' },
  { key: 'channel_settings', section: 'Configuração', label: 'Configurações do canal', write: 'Alterar' },
  { key: 'channel_accounts', section: 'Configuração', label: 'Conta e conexão dos canais', write: 'Conectar e desconectar' },
  { key: 'settings', section: 'Configuração', label: 'Configurações gerais (empresa, lojas, atributos, regras tributárias)', write: 'Alterar' },
  { key: 'api', section: 'Configuração', label: 'Integração por API e chaves', write: 'Criar e revogar chaves' },
] as const;
export type PermKey = (typeof PERMISSIONS)[number]['key'];
export const PERM_KEYS = PERMISSIONS.map((p) => p.key) as PermKey[];
export type Perms = Partial<Record<PermKey, Level>>;

/** Grupos prontos. super_admin tem acesso total (não se edita). */
export const SYSTEM_GROUPS = [
  { key: 'super_admin', name: 'Super admin', full: true },
  { key: 'admin', name: 'Admin', full: false },
  { key: 'operador', name: 'Operador', full: false },
  { key: 'sac', name: 'SAC', full: false },
  { key: 'farmaceutico', name: 'Farmacêutico', full: false },
  { key: 'logistica', name: 'Logística', full: false },
  { key: 'financeiro', name: 'Financeiro', full: false },
] as const;
export type SystemGroupKey = (typeof SYSTEM_GROUPS)[number]['key'];

const R = 'read' as const, W = 'write' as const;
/** Operação: todos veem (pedidos, entregas, perguntas, mensagens, reclamações), e os anúncios e o catálogo também. */
const EVERYONE: Perms = { orders: R, deliveries: R, questions: R, messages: R, claims: R, listings: R, catalog: R };
export const DEFAULT_PERMS: Record<Exclude<SystemGroupKey, 'super_admin'>, Perms> = {
  admin: {
    overview: R, catalog: W, sales: R, orders: W, deliveries: W, queue: W, questions: W, messages: W, claims: W, claims_refund: W,
    prescriptions: W, listings: W, products_listings: W, prices: W, shipping: W, catalog_optin: W, no_catalog: W,
    publications: W, reconcile: W, opportunities: R, buybox: W, finance: W,
  },
  operador: { ...EVERYONE, catalog: W },
  sac: { ...EVERYONE, questions: W, messages: W, claims: W },
  farmaceutico: { ...EVERYONE, prescriptions: W },
  logistica: { ...EVERYONE, orders: W, deliveries: W },
  financeiro: { ...EVERYONE, overview: R, sales: R, finance: W },
};

/** Grupo de cada perfil antigo (memberships.role). */
export const LEGACY_ROLE_GROUP: Partial<Record<TenantRole, SystemGroupKey>> = {
  super_admin: 'super_admin', admin: 'admin', sac: 'sac', pharmacist: 'farmaceutico',
};

export interface Access {
  owner: boolean;
  /** Acesso total (dono ou grupo Super admin). */
  full: boolean;
  /** Marcado como farmacêutico (com CRF): aprova receitas. */
  pharmacist: boolean;
  perms: Perms;
}

/** Soma as permissões dos grupos (vale a maior de cada área). */
export function mergePerms(list: Perms[]): Perms {
  const out: Perms = {};
  for (const p of list) for (const [k, v] of Object.entries(p) as [PermKey, Level][]) {
    if (v === 'write' || (v === 'read' && !out[k])) out[k] = v;
  }
  return out;
}

/** Só as áreas e níveis válidos (o que vem do banco ou da tela). */
export function cleanPerms(p: unknown): Perms {
  const out: Perms = {};
  if (!p || typeof p !== 'object') return out;
  for (const [k, v] of Object.entries(p as Record<string, unknown>)) {
    if ((PERM_KEYS as string[]).includes(k) && (v === 'read' || v === 'write')) out[k as PermKey] = v;
  }
  return out;
}

/** gate: área; 'owner' = só o dono; null = qualquer usuário logado. */
export type Gate = PermKey | 'owner' | null;

export function can(a: Access | null | undefined, gate: Gate, level: Level = 'read'): boolean {
  if (gate === null) return true;
  if (!a) return false;
  if (a.owner) return true;
  if (gate === 'owner') return false;
  if (a.full) return true;
  if (gate === 'prescriptions' && a.pharmacist) return true;
  const p = a.perms[gate];
  return level === 'read' ? !!p : p === 'write';
}

/** Telas do painel (pelo caminho) e a área de cada uma. Caminho que não está aqui: configurações gerais. */
export function permOfPath(path: string): Gate {
  const p = path.split('?')[0]!.replace(/\/+$/, '') || '/';
  const rules: [RegExp, Gate][] = [
    [/^\/(login|cadastro|privacidade|confirmar-email|esqueci-senha|redefinir-senha|em-breve)(\/|$)/, null],
    [/^\/admin(\/|$)/, null], // painel master: a própria tela confere a equipe Berytus
    [/^\/$/, 'overview'],
    [/^\/(catalogo|importacoes)(\/|$)/, 'catalog'],
    [/^\/(vendas|financeiro\/resultados)(\/|$)/, 'sales'],
    [/^\/financeiro(\/|$)/, 'finance'],
    [/^\/pedidos(\/|$)/, 'orders'],
    [/^\/fila(\/|$)/, 'queue'],
    [/^\/perguntas(\/|$)/, 'questions'],
    [/^\/canal\/[a-z0-9]+\/entregas(\/|$)/, 'deliveries'],
    [/^\/canal\/[a-z0-9]+\/receitas(\/|$)/, 'prescriptions'],
    [/^\/canal\/[a-z0-9]+\/perguntas(\/|$)/, 'questions'],
    [/^\/canal\/[a-z0-9]+\/mensagens(\/|$)/, 'messages'],
    [/^\/canal\/[a-z0-9]+\/reclamacoes(\/|$)/, 'claims'],
    [/^\/canal\/[a-z0-9]+\/anuncios(\/|$)/, 'listings'],
    [/^\/canal\/[a-z0-9]+\/produtos(\/|$)/, 'products_listings'],
    [/^\/canal\/[a-z0-9]+\/precos(\/|$)/, 'prices'],
    [/^\/canal\/[a-z0-9]+\/envio(\/|$)/, 'shipping'],
    [/^\/canal\/[a-z0-9]+\/entrar-catalogo(\/|$)/, 'catalog_optin'],
    [/^\/canal\/[a-z0-9]+\/sem-catalogo(\/|$)/, 'no_catalog'],
    [/^\/canal\/[a-z0-9]+\/(publicacoes|publicar|categorias)(\/|$)/, 'publications'],
    [/^\/canal\/[a-z0-9]+\/conciliar(\/|$)/, 'reconcile'],
    [/^\/canal\/[a-z0-9]+\/oportunidades(\/|$)/, 'opportunities'],
    [/^\/canal\/[a-z0-9]+\/concorrentes(\/|$)/, 'buybox'],
    [/^\/canal\/[a-z0-9]+\/configuracoes(\/|$)/, 'channel_settings'],
    [/^\/canal\/[a-z0-9]+$/, 'listings'], // resumo do canal
    [/^\/canal\/[a-z0-9]+\/saude(\/|$)/, 'listings'],
    [/^\/canal\/[a-z0-9]+\/buybox(\/|$)/, 'buybox'],
    [/^\/(canais|app\/(meli|shopee))(\/|$)/, 'channel_accounts'],
    [/^\/configuracoes\/usuarios(\/|$)/, 'owner'],
    [/^\/configuracoes\/api(\/|$)/, 'api'],
    [/^\/(empresa|configuracoes)(\/|$)/, 'settings'],
  ];
  for (const [re, g] of rules) if (re.test(p)) return g;
  return 'settings';
}

/** Primeira tela que o usuário pode abrir. */
export function homeOf(a: Access | null | undefined): string {
  const order: [PermKey, string][] = [
    ['overview', '/'], ['orders', '/pedidos'], ['catalog', '/catalogo'], ['listings', '/canal/mercadolivre/anuncios'],
    ['questions', '/canal/mercadolivre/perguntas'], ['prescriptions', '/canal/mercadolivre/receitas'], ['finance', '/financeiro/conciliacao'], ['sales', '/vendas'],
  ];
  for (const [k, path] of order) if (can(a, k)) return path;
  return '/pedidos';
}

/**
 * Rotas da API do painel (/api/v1) e a área de cada uma, com o nível: GET/HEAD pedem "ver"; o resto, "alterar"
 * (menos as marcadas como leitura, ex.: "Ler agora"). Rota que não está aqui: configurações gerais.
 */
export function permOfApi(method: string, path: string): { gate: Gate; level: Level } {
  const p = path.split('?')[0]!.replace(/^\/api\/v1/, '');
  const read = method === 'GET' || method === 'HEAD';
  // [caminho, área, só para GET?, nível fixo]
  const rules: [RegExp, Gate, boolean?, Level?][] = [
    [/^\/(auth|me|ping|signup|webhooks|media|admin|master)(\/|$)/, null],
    [/^\/account\/compliance-waiver$/, null, true], // banner da liberação provisória: qualquer usuário vê
    [/^\/channels$/, null, true],
    [/^\/notifications(\/|$)/, null], // sino: cada usuário vê e marca as da conta
    // Saúde da conta: ler e "Ler agora" são leitura.
    [/^\/channels\/accounts\/[^/]+\/health\/refresh$/, 'listings', false, 'read'],
    [/^\/channels\/(shopee\/)?accounts\/[^/]+\/health(\/|$)/, 'listings'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/buybox\/refresh$/, 'buybox', false, 'read'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/buybox(\/|$)/, 'buybox'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/ssp\/(refresh|search)$/, 'catalog_optin', false, 'read'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/ssp(\/|$)/, 'catalog_optin'],
    [/^\/account\/(users|groups)(\/|$)/, 'owner'],
    [/^\/account\/api-keys(\/|$)/, 'api'],
    [/^\/account\/(company|settings)(\/|$)/, 'settings'],
    [/^\/catalog\/attributes$/, 'catalog', true],
    [/^\/(catalog\/attributes|attributes|catalog\/tax-rules)(\/|$)/, 'settings'],
    [/^\/stores$/, 'orders', true],
    [/^\/stores(\/|$)/, 'settings'],
    [/^\/catalog\/kits\/margin$/, 'prices'],
    [/^\/catalog\/content-sync(\/|$)/, 'listings'],
    [/^\/catalog\/products\/[^/]+\/meli-draft$/, 'publications'],
    [/^\/(catalog|products|stock)(\/|$)/, 'catalog'],
    [/^\/exports\/catalog\.csv$/, 'catalog'],
    [/^\/exports\/listings\.csv$/, 'listings'],
    [/^\/meli\/domains\//, 'publications'],
    [/^\/meli\//, 'catalog', true],
    [/^\/orders\/(refresh|import)$/, 'settings'],
    [/^\/(orders|packs)(\/|$)/, 'orders'],
    [/^\/deliveries(\/|$)/, 'deliveries'],
    [/^\/prescriptions\/sync$/, 'prescriptions', false, 'read'],
    [/^\/prescriptions(\/|$)/, 'prescriptions'],
    // Atendimento: "Ler agora" e abrir pelo pedido são leitura.
    [/^\/channels\/mercadolivre\/questions\/sync$/, 'questions', false, 'read'],
    [/^\/channels\/(mercadolivre\/questions|questions\/templates)(\/|$)/, 'questions'],
    [/^\/channels\/mercadolivre\/messages\/(sync|open)$/, 'messages', false, 'read'],
    [/^\/channels\/mercadolivre\/messages(\/|$)/, 'messages'],
    [/^\/channels\/mercadolivre\/claims\/(sync|open)$/, 'claims', false, 'read'],
    [/^\/channels\/mercadolivre\/claims(\/|$)/, 'claims'],
    // Anúncios
    [/^\/channels\/mercadolivre\/listings\/bulk-filter$/, 'listings', false, 'read'],
    [/^\/channels\/listings\/[^/]+\/refresh$/, 'listings', false, 'read'],
    [/^\/channels\/listings\/[^/]+\/(link|title-ok)$/, 'reconcile'],
    [/^\/channels\/listings\/[^/]+\/apply-buybox$/, 'buybox'],
    [/^\/channels\/(mercadolivre\/listings|shopee\/listings|listings)(\/|$)/, 'listings'],
    [/^\/channels\/(mercadolivre\/summary|shopee\/overview|ifood\/overview)$/, 'listings'],
    [/^\/channels\/mercadolivre\/products(\/|$)/, 'products_listings'],
    [/^\/channels\/mercadolivre\/(buybox|competitors|market)(\/|$)/, 'buybox'],
    [/^\/channels\/mercadolivre\/opportunities(\/|$)/, 'opportunities'],
    [/^\/channels\/mercadolivre\/shipping(\/|$)/, 'shipping'],
    [/^\/channels\/mercadolivre\/catalog-optin(\/|$)/, 'catalog_optin'],
    [/^\/channels\/(mercadolivre|shopee|ifood)\/(connect|callback)$/, 'channel_accounts'],
    [/^\/channels\/ifood\/accounts(\/|$)/, 'channel_accounts'],
    [/^\/channels\/accounts\/[^/]+$/, 'channel_accounts'],
    [/^\/channels\/accounts\/[^/]+\/settings$/, 'listings', true], // o resumo do canal mostra
    [/^\/channels\/accounts\/[^/]+\/(settings|store|sync|pull)(\/|$)/, 'channel_settings'],
    [/^\/channels\/sync-mode$/, 'channel_settings'],
    [/^\/channels\/accounts\/[^/]+\/(offers|recompute|rules|blocks|block-search)(\/|$)/, 'prices'],
    [/^\/channels\/(blocks|rules)(\/|$)/, 'prices'],
    [/^\/channels\/[^/]+\/offers$/, 'prices'],
    [/^\/channels\/accounts\/[^/]+\/logistics(\/|$)/, 'shipping'],
    [/^\/channels\/accounts\/[^/]+\/(publish|publications)(\/|$)/, 'publications'],
    [/^\/channels\/(accounts\/[^/]+\/own|own)(\/|$)/, 'no_catalog'],
    [/^\/channels\/accounts\/[^/]+\/catalog-lookup$/, 'reconcile'],
    [/^\/channels\/accounts\/[^/]+\/(duplicates|brand-action)(\/|$)/, 'listings'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/listings$/, 'listings'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/orders\/pull$/, 'orders', false, 'read'],
    [/^\/channels\/shopee\/accounts\/[^/]+\/(base\/refresh|pull)$/, 'channel_settings'],
    [/^\/channels\/shopee\/(accounts\/[^/]+|products)(\/|$)/, 'publications'],
    [/^\/sync\/jobs(\/|$)/, 'listings', true],
    [/^\/(dashboard|home|general)(\/|$)/, 'overview'],
    [/^\/goals(\/|$)/, 'settings'], // metas e feriados locais: configuração da conta
    [/^\/sales\/cover$/, 'prices'], // cobrir preço a partir da tela de vendas
    [/^\/sales(\/|$)/, 'sales'],
    [/^\/finance(\/|$)/, 'finance'],
    [/^\/monitor(\/|$)/, 'queue'],
  ];
  for (const [re, gate, getOnly, fixed] of rules) {
    if (!re.test(p)) continue;
    if (getOnly && !read) continue;
    return { gate, level: fixed ?? (read ? 'read' : 'write') };
  }
  return { gate: 'settings', level: read ? 'read' : 'write' };
}

/** Permissões de um grupo: as gravadas pelo dono (override) ou, nos grupos prontos, as padrão. */
export function groupPerms(key: string, override: unknown): Perms {
  if (override != null) return cleanPerms(override);
  return (DEFAULT_PERMS as Record<string, Perms>)[key] ?? {};
}

/** Acesso de um usuário a partir dos grupos dele. */
export function accessOf(groups: { key: string; perms: unknown }[], owner: boolean, pharmacist: boolean): Access {
  return {
    owner, pharmacist,
    full: owner || groups.some((g) => g.key === 'super_admin'),
    perms: mergePerms(groups.filter((g) => g.key !== 'super_admin').map((g) => groupPerms(g.key, g.perms))),
  };
}
